import { EmptyState, EmptyStateIcon, EmptyStateTitle } from '@plato/ui/components/empty-state';
import { Dialog, DialogContent, DialogTitle, type DialogProps } from '@plato/ui/containers/dialog';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from '@plato/ui/primitives/input-group';
import { Spinner } from '@plato/ui/primitives/spinner';
import { SearchIcon, XIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { cn } from '../../../../lib/utils.ts';
import { SearchContext, useSearchContext, type SearchContextValue } from './search-context.ts';

export type SearchProps = ComponentPropsWithRef<'div'> & SearchContextValue;

export function Search({ query, onQueryChange, className, children, ...props }: SearchProps): ReactElement {
    return (
        <SearchContext.Provider value={{ query, onQueryChange }}>
            <div className={cn('flex min-h-0 flex-1 flex-col', className)} {...props}>
                {children}
            </div>
        </SearchContext.Provider>
    );
}

export type SearchInputProps = Omit<ComponentPropsWithRef<'input'>, 'value' | 'onChange'> & {
    clearLabel: string;
};

export function SearchInput({
    className,
    placeholder,
    clearLabel,
    ...props
}: SearchInputProps): ReactElement {
    const { query, onQueryChange } = useSearchContext();

    return (
        <InputGroup>
            <InputGroupAddon>
                <SearchIcon />
            </InputGroupAddon>
            <InputGroupInput
                role="combobox"
                aria-expanded={!!query}
                aria-autocomplete="list"
                // 16px on mobile so iOS Safari doesn't auto-zoom on focus; design's 14px on desktop.
                className={cn('text-ui-lg sm:text-ui-base', className)}
                placeholder={placeholder}
                value={query}
                onChange={(event) => {
                    onQueryChange(event.target.value);
                }}
                {...props}
            />
            {query && (
                <InputGroupAddon align="inline-end">
                    <InputGroupButton
                        aria-label={clearLabel}
                        onClick={() => {
                            onQueryChange('');
                        }}
                    >
                        <XIcon />
                    </InputGroupButton>
                </InputGroupAddon>
            )}
        </InputGroup>
    );
}

export type SearchResultsProps = {
    loading?: boolean;
    empty?: boolean;
    /** Shown when `empty` is true. */
    emptyMessage?: string;
    className?: string;
    ref?: ComponentPropsWithRef<'div'>['ref'];
    children?: ReactNode;
};

export function SearchResults({
    loading = false,
    empty = false,
    emptyMessage,
    className,
    ref,
    children,
}: SearchResultsProps): ReactElement {
    return (
        <div ref={ref} className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', className)}>
            {loading && (
                <div className="flex justify-center py-relaxed">
                    <Spinner />
                </div>
            )}
            {!loading && empty && (
                <EmptyState>
                    <EmptyStateIcon>
                        <SearchIcon />
                    </EmptyStateIcon>
                    <EmptyStateTitle>{emptyMessage}</EmptyStateTitle>
                </EmptyState>
            )}
            {!loading && !empty && (
                <div
                    role="listbox"
                    className="flex flex-col gap-container px-container-cozy pt-container-cozy pb-container-relaxed"
                >
                    {children}
                </div>
            )}
        </div>
    );
}

export type SearchDialogProps = Pick<DialogProps, 'open' | 'defaultOpen' | 'onOpenChange'> & {
    title: string;
    className?: string;
    children?: ReactNode;
};

export function SearchDialog({ title, className, children, ...props }: SearchDialogProps): ReactElement {
    return (
        <Dialog {...props}>
            <DialogContent className={className} snapMode="fullscreen" initialFocus>
                <DialogTitle className="sr-only">{title}</DialogTitle>
                {children}
            </DialogContent>
        </Dialog>
    );
}
